import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    type SliderValue,
    NCoreUIKitTheme,
    ToggleGroup,
    Pagination,
    Breadcrumb,
    TextInput,
    Accordion,
    SelectBox,
    Separator,
    CheckBox,
    Slider,
    Button,
    Badge,
    Card,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ShoppingCart,
    PackagePlus,
    Search,
    Heart,
    User
} from "lucide-react-native";
import {
    Circle,
    Path,
    Rect,
    Svg,
    G
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const CartIcon = createKitIcon(ShoppingCart);

const AddIcon = createKitIcon(PackagePlus);

const SearchIcon = createKitIcon(Search);

const HeartIcon = createKitIcon(Heart);

const UserIcon = createKitIcon(User);

const NAV = [
    "coffees",
    "gear",
    "plans",
    "gift"
];

const FOOTER = [
    "delivery",
    "privacy",
    "contact"
];

const STARS = [
    1,
    2,
    3,
    4,
    5
];

const SORTS = [
    "featured",
    "priceUp",
    "priceDown",
    "rating"
];

const GROUPS = [
    {
        options: [
            {
                id: "ethiopia",
                count: 6
            },
            {
                id: "colombia",
                count: 4
            },
            {
                id: "kenya",
                count: 3
            },
            {
                id: "guatemala",
                count: 3
            },
            {
                id: "brazil",
                count: 5
            }
        ],
        id: "origin"
    },
    {
        options: [
            {
                id: "light",
                count: 9
            },
            {
                id: "medium",
                count: 10
            },
            {
                id: "dark",
                count: 5
            }
        ],
        id: "roast"
    },
    {
        options: [
            {
                id: "fruity",
                count: 11
            },
            {
                id: "chocolate",
                count: 8
            },
            {
                id: "floral",
                count: 5
            },
            {
                id: "nutty",
                count: 7
            }
        ],
        id: "notes"
    }
];

const PRODUCTS = [
    {
        id: "ethiopia",
        reviews: 412,
        badge: "new",
        rating: 4.9,
        price: 285,
        color: "1",
        oldPrice: 0
    },
    {
        id: "colombia",
        reviews: 286,
        rating: 4.7,
        oldPrice: 0,
        price: 260,
        color: "2",
        badge: ""
    },
    {
        badge: "discount",
        oldPrice: 376,
        reviews: 198,
        id: "kenya",
        rating: 4.8,
        price: 320,
        color: "3"
    },
    {
        id: "guatemala",
        reviews: 174,
        rating: 4.6,
        oldPrice: 0,
        price: 255,
        color: "4",
        badge: ""
    },
    {
        id: "brazil",
        reviews: 531,
        rating: 4.7,
        oldPrice: 0,
        price: 215,
        color: "5",
        badge: ""
    },
    {
        id: "rwanda",
        badge: "new",
        rating: 4.8,
        reviews: 67,
        price: 298,
        color: "6",
        oldPrice: 0
    },
    {
        badge: "discount",
        id: "costaRica",
        oldPrice: 324,
        reviews: 142,
        rating: 4.7,
        price: 275,
        color: "7"
    },
    {
        id: "sumatra",
        reviews: 203,
        rating: 4.5,
        oldPrice: 0,
        price: 245,
        color: "8",
        badge: ""
    },
    {
        badge: "limited",
        id: "panama",
        reviews: 38,
        oldPrice: 0,
        price: 890,
        color: "1",
        rating: 5
    }
];

const INITIAL_FILTERS = [
    "ethiopia",
    "light",
    "fruity"
];

const STAR_PATH = "M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26Z";

const BAG_BODY = "M38 36h84l9 126a7 7 0 0 1-7 7H36a7 7 0 0 1-7-7z";

const BAG_SHOULDER = "M38 36h84l1.4 20H36.6z";

const BAG_CAP = "M44 22c0-4 4-6 8-6h56c4 0 8 2 8 6v14H44z";

const BAG_SHINE = "M120 60l8 100";

const BAG_LEAF = "M74 106c2-8 8-12 14-12-1 7-5 12-14 12z";

const MARK_BAG = "M8 11h12l-1.4 8.2a2 2 0 0 1-2 1.8h-5.2a2 2 0 0 1-2-1.8z";

const MARK_HANDLE = "M10.5 11a3.5 3.5 0 0 1 7 0";

const PAGE_SIZE = 9;

const stylesheet = StyleSheet.create({
    strike: {
        textDecorationLine: "line-through"
    },
    spread: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    footer: {
        justifyContent: "space-between",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    priceBlock: {
        justifyContent: "flex-end"
    },
    announcement: {
        justifyContent: "center",
        alignItems: "center"
    },
    art: {
        justifyContent: "center",
        alignItems: "center",
        aspectRatio: 1.25,
        width: "100%"
    },
    columns: {
        alignItems: "flex-start"
    },
    bagArt: {
        aspectRatio: 160 / 180,
        height: "86%"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    wrapper: {
        alignSelf: "center",
        width: "100%"
    },
    badgeSlot: {
        position: "absolute"
    },
    heartSlot: {
        position: "absolute"
    },
    cardCell: {
        overflow: "hidden",
        flexBasis: 0,
        minWidth: 0,
        flexGrow: 1
    },
    grow: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    full: {
        width: "100%"
    },
    search: {
        minWidth: 0,
        flex: 1
    }
});

type BagProps = {
    paper: string;
    fill: string;
    ink: string;
};

const Bag = ({
    paper,
    fill,
    ink
}: BagProps) => <G>
    <Path
        d={BAG_BODY}
        fill={fill}
    />
    <Path
        d={BAG_SHOULDER}
        opacity={0.16}
        fill={ink}
    />
    <Path
        fill={fill}
        d={BAG_CAP}
    />
    <Rect
        opacity={0.22}
        height={6}
        width={72}
        fill={ink}
        rx={2}
        x={44}
        y={30}
    />
    <Path
        strokeLinecap="round"
        strokeWidth={6}
        stroke={paper}
        opacity={0.14}
        d={BAG_SHINE}
        fill="none"
    />
    <Rect
        fill={paper}
        height={70}
        width={68}
        rx={6}
        x={46}
        y={78}
    />
    <Circle
        strokeWidth={4}
        stroke={fill}
        fill="none"
        cy={102}
        cx={80}
        r={12}
    />
    <Path
        d={BAG_LEAF}
        fill={fill}
    />
    <Rect
        opacity={0.8}
        height={4}
        width={44}
        fill={ink}
        y={124}
        rx={2}
        x={58}
    />
    <Rect
        opacity={0.35}
        height={3}
        width={28}
        fill={ink}
        rx={1.5}
        y={132}
        x={66}
    />
</G>;

type StarsProps = {
    empty: string;
    value: number;
    size: number;
    fill: string;
};

const Stars = ({
    empty,
    value,
    size,
    fill
}: StarsProps) => <Svg
    viewBox="0 0 120 24"
    width={size * 5}
    height={size}
>
    {
        STARS.map((star) => <Path
            fill={star <= Math.round(value) ? fill : empty}
            transform={`translate(${(star - 1) * 24} 0)`}
            d={STAR_PATH}
            key={star}
        />)
    }
</Svg>;

const getOptionTitle = (option: {
    name: string;
}) => option.name;

const getOptionKey = (option: {
    id: string;
}) => option.id;

const Store01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        filters,
        setFilters
    ] = useState<Array<string>>(INITIAL_FILTERS);

    const [
        form,
        setForm
    ] = useState("bean");

    const [
        price,
        setPrice
    ] = useState<SliderValue>([
        200,
        600
    ]);

    const [
        sort,
        setSort
    ] = useState("featured");

    const [
        favorites,
        setFavorites
    ] = useState<Array<string>>([]);

    const [
        cartCount,
        setCartCount
    ] = useState(3);

    const [
        page,
        setPage
    ] = useState(1);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isStacked = width < shapeConfigs.wideBreakpoint;

    const columns = isCompact ? 1 : isStacked ? 2 : 3;

    const categorical = colors.chart.categorical;

    const categoricalArea = colors.chart.categoricalArea;

    const ink = colors.content.container.inverse;

    const paper = colors.content.container.default;

    const money = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const ratingFormat = new Intl.NumberFormat(activeLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });

    const handlePress = () => undefined;

    const getColor = (key: string) => categorical[key as keyof typeof categorical];

    const getArea = (key: string) => categoricalArea[key as keyof typeof categoricalArea];

    const handleFormChange = (value: string | Array<string>) => setForm(typeof value === "string" && value !== "" ? value : form);

    const handleFilterToggle = (id: string) => () => setFilters((current) => current.includes(id) ? current.filter((item) => item !== id) : [
        ...current,
        id
    ]);

    const handleClear = () => setFilters([]);

    const handlePriceChange = (value: SliderValue) => setPrice(value);

    const handleSortChange = (items: Array<{
        __key: string;
    }>) => setSort(items[0]?.__key ?? "featured");

    const handleFavorite = (id: string) => () => setFavorites((current) => current.includes(id) ? current.filter((item) => item !== id) : [
        ...current,
        id
    ]);

    const handleAdd = () => setCartCount((current) => current + 1);

    const priceText = Array.isArray(price) ? `${money.format(price[0] ?? 0)} – ${money.format(price[1] ?? 0)}` : money.format(price);

    const sortOptions = SORTS.map((key) => ({
        name: t(`blocks.store01.sort.${key}`),
        id: key
    }));

    const sortedProducts = [
        ...PRODUCTS
    ].sort((a, b) => {
        if(sort === "priceUp") {
            return a.price - b.price;
        }

        if(sort === "priceDown") {
            return b.price - a.price;
        }

        if(sort === "rating") {
            return b.rating - a.rating;
        }

        return 0;
    });

    const productRows = Array.from({
        length: Math.ceil(sortedProducts.length / columns)
    }, (_, index) => sortedProducts.slice(index * columns, index * columns + columns));

    const renderNav = (key: string, index: number) => <Button
        variant={index === 0 ? "tonal" : "ghost"}
        title={t(`blocks.store01.nav.${key}`)}
        onPress={handlePress}
        type="neutral"
        size="small"
        key={key}
    />;

    const renderChip = (id: string) => <Chip
        title={t(`blocks.store01.filter.${id}`)}
        onClose={handleFilterToggle(id)}
        isClosable={true}
        variant="tonal"
        type="neutral"
        key={id}
    />;

    const renderOption = (option: {
        count: number;
        id: string;
    }) => <View
        style={[
            stylesheet.spread,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={option.id}
    >
        <CheckBox
            title={t(`blocks.store01.filter.${option.id}`)}
            isChecked={filters.includes(option.id)}
            onPress={handleFilterToggle(option.id)}
            style={stylesheet.grow}
        />
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {option.count}
        </Text>
    </View>;

    const renderGroup = (group: typeof GROUPS[number]) => <View
        style={{
            gap: spaces.spacingSm
        }}
        key={group.id}
    >
        <Separator
            length="100%"
        />
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {t(`blocks.store01.group.${group.id}`)}
        </Text>
        {group.options.map(renderOption)}
    </View>;

    const renderFilters = () => <View
        style={{
            gap: spaces.spacingMd
        }}
    >
        <View
            style={stylesheet.spread}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {t("blocks.store01.filters")}
            </Text>
            <Button
                title={t("blocks.store01.clear")}
                onPress={handleClear}
                variant="link"
                size="small"
            />
        </View>
        <View
            style={{
                gap: spaces.spacingXs
            }}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {t("blocks.store01.form")}
            </Text>
            <ToggleGroup
                title={t("blocks.store01.form")}
                onValueChange={handleFormChange}
                attachBehaviour="attached"
                isClearEnabled={false}
                variant="outline"
                size="small"
                value={form}
            >
                <ToggleGroup.Item
                    title={t("blocks.store01.formBean")}
                    value="bean"
                />
                <ToggleGroup.Item
                    title={t("blocks.store01.formGround")}
                    value="ground"
                />
            </ToggleGroup>
        </View>
        {GROUPS.map(renderGroup)}
        <Separator
            length="100%"
        />
        <Slider
            title={t("blocks.store01.price")}
            onValueChange={handlePriceChange}
            formatValueText={money.format}
            isShowMinMaxLabels={true}
            isShowValue={false}
            value={price}
            step={10}
            min={100}
            max={900}
        />
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {priceText}
        </Text>
        <Card
            variant="filled"
        >
            <Card.Header>
                <Card.Title>
                    {t("blocks.store01.quiz")}
                </Card.Title>
                <Card.Description>
                    {t("blocks.store01.quizText")}
                </Card.Description>
            </Card.Header>
        </Card>
    </View>;

    const renderProduct = (product: typeof PRODUCTS[number]) => {
        const isFavorite = favorites.includes(product.id);

        return <Card
            style={stylesheet.cardCell}
            variant="outline"
            key={product.id}
        >
            <Card.Content
                isFlush={true}
            >
                <View>
                    <View
                        style={[
                            stylesheet.art,
                            {
                                backgroundColor: getArea(product.color)
                            }
                        ]}
                    >
                        <Svg
                            accessibilityLabel={t(`blocks.store01.product.${product.id}.name`)}
                            style={stylesheet.bagArt}
                            viewBox="0 0 160 180"
                            role="img"
                        >
                            <Bag
                                fill={getColor(product.color)}
                                paper={paper}
                                ink={ink}
                            />
                        </Svg>
                    </View>
                    {
                        product.badge === "" ?
                            null
                        :
                            <View
                                style={[
                                    stylesheet.badgeSlot,
                                    {
                                        left: spaces.spacingSm,
                                        top: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Badge
                                    type={product.badge === "new" ? "primary" : product.badge === "discount" ? "danger" : "neutral"}
                                    variant={product.badge === "limited" ? "outline" : "tonal"}
                                    title={t(`blocks.store01.badge.${product.badge}`)}
                                />
                            </View>
                    }
                    <View
                        style={[
                            stylesheet.heartSlot,
                            {
                                right: spaces.spacingXs,
                                top: spaces.spacingXs
                            }
                        ]}
                    >
                        <Button
                            accessibilityLabel={t(isFavorite ? "blocks.store01.unfavorite" : "blocks.store01.favorite")}
                            variant={isFavorite ? "filled" : "tonal"}
                            type={isFavorite ? "danger" : "neutral"}
                            onPress={handleFavorite(product.id)}
                            icon={HeartIcon}
                            size="small"
                        />
                    </View>
                </View>
            </Card.Content>
            <Card.Content>
                <View
                    style={{
                        gap: spaces.spacingSm
                    }}
                >
                    <View>
                        <Text
                            variant="titleMediumSize"
                            numberOfLines={1}
                            color="high"
                        >
                            {t(`blocks.store01.product.${product.id}.name`)}
                        </Text>
                        <Text
                            variant="bodySmallSize"
                            numberOfLines={1}
                            color="mid"
                        >
                            {t(`blocks.store01.product.${product.id}.origin`)}
                        </Text>
                    </View>
                    <Text
                        variant="bodyMediumSize"
                        numberOfLines={2}
                        color="mid"
                    >
                        {t(`blocks.store01.product.${product.id}.notes`)}
                    </Text>
                    <View
                        accessibilityLabel={t("blocks.store01.ratingLabel", [
                            ratingFormat.format(product.rating),
                            product.reviews
                        ])}
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                        accessibilityRole="image"
                    >
                        <Stars
                            empty={colors.content.border.default}
                            fill={categorical["4"]}
                            size={iconSizes.small}
                            value={product.rating}
                        />
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {`${ratingFormat.format(product.rating)} (${product.reviews})`}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.spread,
                            {
                                alignItems: "flex-end",
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.priceBlock,
                                {
                                    minHeight: controlHeights.large + spaces.spacingMd
                                }
                            ]}
                        >
                            {
                                product.oldPrice > 0 ?
                                    <Text
                                        style={stylesheet.strike}
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {money.format(product.oldPrice)}
                                    </Text>
                                :
                                    null
                            }
                            <Text
                                variant="titleLargeSize"
                                color="high"
                            >
                                {money.format(product.price)}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.store01.weight")}
                            </Text>
                        </View>
                        <Button
                            accessibilityLabel={t("blocks.store01.add", [
                                t(`blocks.store01.product.${product.id}.name`)
                            ])}
                            onPress={handleAdd}
                            variant="outline"
                            type="neutral"
                            icon={AddIcon}
                            size="small"
                        />
                    </View>
                </View>
            </Card.Content>
        </Card>;
    };

    const renderRow = (row: Array<typeof PRODUCTS[number]>, index: number) => <View
        style={[
            stylesheet.row,
            stylesheet.columns,
            {
                gap: spaces.spacingMd
            }
        ]}
        key={index}
    >
        {row.map(renderProduct)}
        {
            Array.from({
                length: columns - row.length
            }, (_, filler) => <View
                style={stylesheet.cardCell}
                key={filler}
            />)
        }
    </View>;

    const renderFooterLink = (key: string) => <Button
        title={t(`blocks.store01.footer.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    const renderContent = (children: ReactNode) => <View
        style={[
            stylesheet.wrapper,
            {
                maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg
            }
        ]}
    >
        {children}
    </View>;

    return <ScrollView>
        <View
            style={[
                stylesheet.announcement,
                {
                    backgroundColor: colors.content.container.inverse,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="contrastHigh"
            >
                {t("blocks.store01.announcement")}
            </Text>
        </View>
        <View
        >
            {
                renderContent(<View
                    style={[
                        stylesheet.row,
                        {
                            paddingVertical: spaces.spacingSm,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Svg
                            height={iconSizes.large * 1.5}
                            width={iconSizes.large * 1.5}
                            viewBox="0 0 28 28"
                        >
                            <Circle
                                fill={ink}
                                cx={14}
                                cy={14}
                                r={14}
                            />
                            <Path
                                d={MARK_BAG}
                                fill={paper}
                            />
                            <Path
                                strokeLinecap="round"
                                strokeWidth={1.6}
                                d={MARK_HANDLE}
                                stroke={paper}
                                fill="none"
                            />
                        </Svg>
                        <Text
                            variant="titleLargeSize"
                            color="high"
                        >
                            {t("blocks.store01.brand")}
                        </Text>
                    </View>
                    {
                        isStacked ?
                            null
                        :
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingXxs
                                    }
                                ]}
                            >
                                {NAV.map(renderNav)}
                            </View>
                    }
                    <TextInput
                        accessibilityLabel={t("blocks.store01.search")}
                        placeholder={t("blocks.store01.search")}
                        style={stylesheet.search}
                        icon={SearchIcon}
                        size="small"
                    />
                    {
                        isCompact ?
                            null
                        :
                            <Button
                                accessibilityLabel={t("blocks.store01.account")}
                                onPress={handlePress}
                                variant="ghost"
                                icon={UserIcon}
                                type="neutral"
                                size="small"
                            />
                    }
                    <Badge.Indicator
                        count={cartCount}
                        type="primary"
                    >
                        <Button
                            accessibilityLabel={t("blocks.store01.cart")}
                            onPress={handlePress}
                            variant="ghost"
                            icon={CartIcon}
                            type="neutral"
                            size="small"
                        />
                    </Badge.Indicator>
                </View>)
            }
            <Separator
                length="100%"
            />
        </View>
        {
            renderContent(<View
                style={{
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }}
            >
                <Breadcrumb
                    accessibilityLabel={t("blocks.store01.breadcrumb")}
                    compactBehaviour="collapse"
                >
                    <Breadcrumb.List>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.store01.home")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.store01.nav.coffees")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Page
                                title={t("blocks.store01.title")}
                            />
                        </Breadcrumb.Item>
                    </Breadcrumb.List>
                </Breadcrumb>
                <View
                    style={[
                        stylesheet.spread,
                        stylesheet.wrap,
                        {
                            alignItems: "flex-end",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.grow,
                            {
                                minWidth: shapeConfigs.fieldMinWidth,
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            variant="headlineMediumSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.store01.title")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.store01.lead", [
                                24,
                                PAGE_SIZE
                            ])}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.store01.sortBy")}
                        </Text>
                        <View
                            style={{
                                minWidth: shapeConfigs.sideListWidth
                            }}
                        >
                            <SelectBox
                                accessibilityLabel={t("blocks.store01.sortBy")}
                                titleExtractor={getOptionTitle}
                                onChange={handleSortChange}
                                keyExtractor={getOptionKey}
                                spreadBehaviour="stretch"
                                defaultValue="featured"
                                data={sortOptions}
                            />
                        </View>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.columns,
                        {
                            flexDirection: isStacked ? "column" : "row",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={{
                            width: isStacked ? "100%" : shapeConfigs.sideListWidth + spaces.spacingMd
                        }}
                    >
                        {
                            isStacked ?
                                <Accordion
                                    variant="contained"
                                >
                                    <Accordion.Item
                                        value="filters"
                                    >
                                        <Accordion.Trigger
                                            title={t("blocks.store01.filters")}
                                        />
                                        <Accordion.Content>
                                            {renderFilters()}
                                        </Accordion.Content>
                                    </Accordion.Item>
                                </Accordion>
                            :
                                renderFilters()
                        }
                    </View>
                    <View
                        style={[
                            stylesheet.grow,
                            stylesheet.full,
                            {
                                gap: spaces.spacingLg
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.wrap,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            {filters.map(renderChip)}
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingLg
                            }}
                        >
                            {productRows.map(renderRow)}
                        </View>
                        <View
                            style={[
                                stylesheet.spread,
                                stylesheet.wrap,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.store01.range", [
                                    PAGE_SIZE,
                                    24
                                ])}
                            </Text>
                            <Pagination
                                onPageChange={setPage}
                                pageSize={PAGE_SIZE}
                                totalItems={24}
                                size="small"
                                page={page}
                            />
                        </View>
                    </View>
                </View>
            </View>)
        }
        <View>
            <Separator
                length="100%"
            />
            {
                renderContent(<View
                    style={[
                        stylesheet.footer,
                        {
                            paddingVertical: spaces.spacingLg,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.store01.copyright")}
                    </Text>
                    <View
                        style={[
                            stylesheet.row,
                            stylesheet.wrap,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {FOOTER.map(renderFooterLink)}
                    </View>
                </View>)
            }
        </View>
    </ScrollView>;
};
export default Store01;
